/**
 * MechanicJourney — Scavenger Hunt / multi-step quest visualization.
 *
 * Renders an S-curve journey of N nodes (typically 3–6). Done nodes are
 * solid + red; the active node has a pulse ring; remaining nodes render
 * dashed. The same component handles three slot variants:
 *   - marquee: full SVG path with node labels under each step
 *   - tile:    linear node strip (no curve) + step count
 *   - browse:  compressed SVG path, no labels
 */

import React, {useId} from "react";
import "./MechanicJourney.scss";

export interface JourneyStep {
    id: string;
    label: string;
    done: boolean;
    active: boolean;
}

interface MechanicJourneyProps {
    variant: "marquee" | "tile" | "browse";
    steps: JourneyStep[];
    /** Status copy shown beneath the path in marquee state (e.g. "On chapter 3 of 4 · 4,521 hunting"). */
    statusLine?: React.ReactNode;
}

/*
 * Evenly spaced node x-positions across the viewBox. Every node sits on the
 * same horizontal baseline (`y = height / 2`); the winding rhythm comes from
 * the curves BETWEEN nodes, which alternate bulging up and down — see
 * buildSegmentPath. Pure function of step count + viewBox width.
 */
function nodeXs(count: number, padding: number, width: number): number[] {
    if (count <= 1) return [width / 2];
    const usable = width - padding * 2;
    const gap = usable / (count - 1);
    return Array.from({length: count}, (_, i) => padding + gap * i);
}

/*
 * Generate the curved S-path between consecutive nodes. Each segment is
 * a cubic curve with control points that lift toward / dip away from the
 * baseline, producing the winding rhythm.
 */
function buildSegmentPath(x1: number, x2: number, y: number, isUpCurve: boolean): string {
    const lift = isUpCurve ? -26 : 26;
    const cx1 = x1 + (x2 - x1) * 0.3;
    const cx2 = x1 + (x2 - x1) * 0.7;
    return `M ${x1} ${y} C ${cx1} ${y + lift}, ${cx2} ${y + lift}, ${x2} ${y}`;
}

export const MechanicJourney: React.FC<MechanicJourneyProps> = ({variant, steps, statusLine}) => {
    /*
     * useId gives every instance a unique filter id even when multiple
     * journeys of the same variant render on the same page (e.g., a
     * DropEngagementStrip with marquee + multiple tile siblings). A
     * static `mj-glow-${variant}` would produce duplicate SVG <defs id="">
     * across instances — browsers resolve to the first match, so all
     * later instances reference the wrong filter definition.
     */
    const filterId = `mj-glow-${useId().replace(/[:]/g, "")}`;

    /*
     * The active-node pulse is SVG SMIL, not CSS, so a
     * `@media (prefers-reduced-motion: reduce)` rule cannot stop it — the
     * <animate> elements have to be omitted outright. Read once at render
     * rather than subscribed: the OS setting effectively never flips mid-session,
     * and the node's colour + stroke already convey "active" without the pulse.
     */
    const reduceMotion = typeof window !== "undefined" &&
        !!window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;

    /*
     * Empty-step early return — `buildJourneySteps` upstream can return
     * an empty array for a milestoneless questConfig. Without this guard,
     * the SVG renders blank with no signal that something is missing.
     */
    if (steps.length === 0) {
        return <div className={`mechanic-journey mechanic-journey--${variant} mechanic-journey--empty`} role="img" aria-label="No steps yet" />;
    }

    if (variant === "tile") {
        return (
            <span className="mechanic-journey mechanic-journey--tile" role="img" aria-label={`${steps.filter(s => s.done).length} of ${steps.length} steps done`}>
                {steps.map((step, i) =>
                    <React.Fragment key={step.id}>
                        <span className={`mechanic-journey__tile-node${step.done ? " is-done" : ""}${step.active ? " is-active" : ""}`} />
                        {i < steps.length - 1 &&
                            <span className={`mechanic-journey__tile-line${step.done ? " is-done" : ""}`} />
                        }
                    </React.Fragment>)}
            </span>
        );
    }

    /*
     * SVG sizing — marquee uses 360x80 (room for labels), browse a
     * shorter 280x60 (no labels). Internal padding keeps end nodes off
     * the edges so the pulse ring doesn't clip.
     */
    const width = variant === "browse" ? 280 : 360;
    const height = variant === "browse" ? 60 : 80;
    const y = height / 2;
    const xs = nodeXs(steps.length, 22, width);

    return (
        <div className={`mechanic-journey mechanic-journey--${variant}`}>
            <svg className="mechanic-journey__svg" viewBox={`0 0 ${width} ${height}`} role="img" aria-label="Journey progress">
                <defs>
                    <filter id={filterId}>
                        <feGaussianBlur stdDeviation="2.5" result="blur" />
                        <feMerge>
                            <feMergeNode in="blur" />
                            <feMergeNode in="SourceGraphic" />
                        </feMerge>
                    </filter>
                </defs>

                {/* Path segments — done segments solid; remaining dashed */}
                {xs.slice(0, -1).map((x, i) => {
                    const nextX = xs[i + 1];
                    /*
                     * A segment is "reached" once the step it leaves is done —
                     * that is the whole condition. (A `segmentDone` that also
                     * required the NEXT step to be done used to feed the dash
                     * check, but it is strictly implied by this, so it never
                     * changed a single render.)
                     */
                    const segmentReached = steps[i].done;
                    const isUp = i % 2 === 0;
                    const d = buildSegmentPath(x, nextX, y, isUp);
                    return (
                        <path
                            key={`seg-${i}`}
                            d={d}
                            stroke={segmentReached ? "#F1161D" : "rgba(255,255,255,0.15)"}
                            strokeWidth={segmentReached ? 2.5 : 2}
                            fill="none"
                            strokeDasharray={segmentReached ? "none" : "5,5"}
                            filter={segmentReached ? `url(#${filterId})` : undefined}
                        />
                    );
                })}

                {/* Nodes */}
                {steps.map((step, i) => {
                    const x = xs[i];
                    if (step.active) {
                        return (
                            <g key={step.id}>
                                <circle cx={x} cy={y} r={10} fill="rgba(241,22,29,0.2)" stroke="#F1161D" strokeWidth={2} filter={`url(#${filterId})`} />
                                <circle cx={x} cy={y} r={14} fill="none" stroke="rgba(241,22,29,0.4)" strokeWidth={1}>
                                    {!reduceMotion && <>
                                        <animate attributeName="r" values="10;18;10" dur="1.8s" repeatCount="indefinite" />
                                        <animate attributeName="opacity" values="0.8;0;0.8" dur="1.8s" repeatCount="indefinite" />
                                    </>}
                                </circle>
                            </g>
                        );
                    }
                    if (step.done) {
                        return (
                            <g key={step.id}>
                                <circle cx={x} cy={y} r={9} fill="#F1161D" filter={`url(#${filterId})`} />
                                <text x={x} y={y + 4} fontFamily="IBM Plex Mono" fontSize="9" fill="#fff" textAnchor="middle" fontWeight="700">✓</text>
                            </g>
                        );
                    }
                    /* Remaining */
                    return (
                        <circle
                            key={step.id}
                            cx={x}
                            cy={y}
                            r={9}
                            fill="none"
                            stroke="rgba(255,255,255,0.25)"
                            strokeWidth={1.5}
                            strokeDasharray="3,2"
                        />
                    );
                })}
            </svg>

            {variant === "marquee" &&
                <div className="mechanic-journey__legend">
                    {steps.map(step =>
                        <span
                            key={step.id}
                            className={`mechanic-journey__legend-item${step.done ? " is-done" : ""}${step.active ? " is-active" : ""}`}
                        >
                            {step.label}
                        </span>)}
                </div>
            }

            {variant === "marquee" && statusLine &&
                <div className="mechanic-journey__status">{statusLine}</div>
            }
        </div>
    );
};
